<script lang="ts">
  /**
   * RecentMoveMenuItem - a contextual "Move to <project>" shortcut for task menus.
   *
   * Shows the most recent project a task was moved to (client-side, short-lived),
   * so users can keep moving tasks to the same project without opening the
   * searchable picker again. The store enforces the TTL (self-expires), so this
   * component is purely reactive — no countdown timers.
   *
   * Self-contained styling: Svelte scoped styles don't cross component
   * boundaries, so this replicates the shared `.menu-item` look. Hosts with a
   * different menu scale can override `--menu-item-font-size`,
   * `--menu-item-padding`, `--menu-item-gap` and `--menu-item-radius`.
   */
  import { recentMove } from '../lib/stores/recentMove';
  import Icon from './Icon.svelte';

  interface Props {
    /** The project the current task belongs to — the shortcut is hidden for it. */
    currentProjectId: string;
    /** Called when the user activates the shortcut. */
    onMove: (projectId: string, projectTitle: string) => void;
  }

  let { currentProjectId, onMove }: Props = $props();

  const recent = $derived($recentMove);
  const visible = $derived(!!recent && recent.projectId !== currentProjectId);
</script>

{#if visible && recent}
  <button
    type="button"
    class="recent-move-item"
    title={`Move to "${recent.projectTitle}"`}
    onclick={() => onMove(recent.projectId, recent.projectTitle)}
  >
    <span class="recent-icon"><Icon name="recent-move" size={14} /></span>
    <span class="recent-label">Move to "{recent.projectTitle}"</span>
  </button>
{/if}

<style>
  .recent-move-item {
    display: flex;
    align-items: flex-start;
    gap: var(--menu-item-gap, 0.375rem);
    width: 100%;
    padding: var(--menu-item-padding, 0.375rem 0.5rem);
    font-size: var(--menu-item-font-size, 0.6875rem);
    font-family: inherit;
    color: #374151;
    background: none;
    border: none;
    border-radius: var(--menu-item-radius, 0);
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .recent-move-item:hover {
    background: #f3f4f6;
  }

  .recent-icon {
    display: flex;
    flex-shrink: 0;
    color: #6b7280;
    /* Center the icon on the first line of a possibly two-line label. */
    height: 1.3em;
    align-items: center;
  }

  .recent-label {
    /* Clamp long project names to 2 lines with an ellipsis on the last line. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
    line-height: 1.3;
    word-break: break-word;
  }
</style>
